import React, { useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent,
  Avatar, TextField, InputAdornment, MenuItem,
  ToggleButtonGroup, ToggleButton, Select, FormControl, InputLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import listings from './staticListings';
import { useNavigate } from 'react-router-dom';
import logo from './memphispassive.jpg';

export default function PublicListingsPage() {
  const navigate = useNavigate();
  const [view, setView] = useState('list');
  const [filters, setFilters] = useState({
    beds: '',
    baths: '',
    rent: '',
    type: '',
    zip: ''
  });

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const filteredListings = listings.filter(l => {
    const bedMatch = filters.beds ? l.beds.includes(filters.beds) : true;
    const bathMatch = filters.baths ? l.baths.includes(filters.baths) : true;
    const rentMatch = filters.rent ? l.rent <= parseInt(filters.rent) : true;
    const zipMatch = filters.zip ? l.unit.includes(filters.zip) : true;
    return bedMatch && bathMatch && rentMatch && zipMatch;
  });

  return (
    <Box>
      {/* HEADER */}
      <Box
        sx={{
          backgroundColor: '#fff',
          borderBottom: '1px solid #ddd',
          px: 4,
          py: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <img src={logo} alt="Memphis Passive Logo" style={{ height: 80 }} />
          <Box sx={{ display: 'flex', gap: 4, fontWeight: 500 }}>
            {['Home', 'Rentals', 'Contact Us', 'Apply Now', 'Renter Qualification'].map(label => (
              <Typography
                key={label}
                variant="body1"
                sx={{
                  cursor: 'pointer',
                  transition: 'color 0.2s',
                  '&:hover': { color: '#1976d2' }
                }}
              >
                {label}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>

      {/* MAIN SECTION */}
      <Box sx={{ px: 4, py: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">Search Available Rentals</Typography>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(e, nextView) => nextView && setView(nextView)}
          >
            <ToggleButton value="list"><ViewListIcon /></ToggleButton>
            <ToggleButton value="grid"><GridViewIcon /></ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* FILTERS */}
        <Grid container spacing={2} mb={4}>
          {['beds', 'baths', 'rent', 'type'].map(field => (
            <Grid item xs={6} md={2} key={field}>
              <FormControl fullWidth size="small">
                <InputLabel>{field[0].toUpperCase() + field.slice(1)}</InputLabel>
                <Select name={field} value={filters[field]} onChange={handleChange} label={field}>
                  <MenuItem value="">All</MenuItem>
                  {field === 'beds' && ['1 Bed', '2 Bed', '3 Bed', '4 Bed'].map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                  {field === 'baths' && ['1 Bath', '2 Bath'].map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                  {field === 'rent' && [1000, 1200, 1400, 1600].map(opt => <MenuItem key={opt} value={opt}>{`Under $${opt}`}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          ))}
          <Grid item xs={6} md={4}>
            <TextField
              name="zip"
              value={filters.zip}
              onChange={handleChange}
              fullWidth
              placeholder="ZIP or city/state"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>

        {/* LISTINGS */}
        <Grid container spacing={3}>
          {filteredListings.map(listing => (
            <Grid item xs={12} md={view === 'grid' ? 3 : 12} key={listing.id}>
              <Card
                sx={{
                  display: view === 'list' ? 'flex' : 'block',
                  cursor: 'pointer',
                  borderRadius: 2,
                  '&:hover': { boxShadow: 6 },
                  overflow: 'hidden'
                }}
onClick={() => navigate(`/listings/${listing.id}`, { state: { listing } })}
              >
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 2
                }}>
                  <Avatar
                    src={listing.thumbnail}
                    variant="rounded"
                    sx={{
                      width: view === 'list' ? 240 : '100%',
                      height: view === 'list' ? 160 : 180,
                      borderRadius: 2
                    }}
                  />
                </Box>
                <CardContent sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {listing.unit.split('-')[0]}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {listing.beds} | {listing.baths} | {listing.size} sqft
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Section 8 and HUD-VASH Vouchers Accepted! Check out this unit at {listing.unit.split('-')[0]}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" color="primary">${listing.rent.toLocaleString()}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      AVAILABLE {new Date(listing.availableDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
